import React from 'react';
import { useGlobalVariables } from '../contexts/GlobalVariablesContext';

/**
 * GlobalVariables 디버그 패널
 * 현재 변수 상태와 구독 관계를 실시간으로 표시
 */
const GlobalVariablesDebugPanel = () => {
  const { 
    variables, 
    getSubscriptions, 
    debugMode, 
    clearVariables 
  } = useGlobalVariables();
  
  // 패널 표시/접기 상태 (관리자 토글과 별개, 로컬 UI 상태)
  const [isVisible, setIsVisible] = React.useState(() => {
    try {
      const stored = localStorage.getItem('enerwiser.debug.globalVariables.visible');
      // 기본은 보이기(true). 명시적으로 'false' 저장된 경우만 숨김으로 시작
      return stored !== 'false';
    } catch (e) {
      return true;
    }
  });
  
  const setVisible = (v) => {
    setIsVisible(v);
    try {
      localStorage.setItem('enerwiser.debug.globalVariables.visible', v ? 'true' : 'false');
      window.dispatchEvent(new CustomEvent('enerwiser:debug-panel-visibility', { detail: v }));
    } catch (e) {
      // noop
    }
  };

  // 외부에서 아이콘 클릭으로 열고 닫을 수 있도록 이벤트 수신
  React.useEffect(() => {
    const onVisibility = (e) => {
      if (typeof e.detail === 'boolean') {
        setIsVisible(e.detail);
        try {
          localStorage.setItem('enerwiser.debug.globalVariables.visible', e.detail ? 'true' : 'false');
        } catch (err) {
          // noop
        }
      }
    };
    window.addEventListener('enerwiser:debug-panel-visibility', onVisibility);
    return () => window.removeEventListener('enerwiser:debug-panel-visibility', onVisibility);
  }, []);

  // 드래그 이동 좌표 (좌상단 기준)
  const [position, setPosition] = React.useState(() => {
    try {
      const x = parseInt(localStorage.getItem('enerwiser.debug.globalVariables.posX') || '10', 10);
      const y = parseInt(localStorage.getItem('enerwiser.debug.globalVariables.posY') || '80', 10);
      return { x, y };
    } catch (e) {
      return { x: 10, y: 80 };
    }
  });
  const dragRef = React.useRef({ dragging: false, startX: 0, startY: 0, origX: 0, origY: 0 });

  const onMouseDownHeader = (e) => {
    e.preventDefault();
    dragRef.current.dragging = true;
    dragRef.current.startX = e.clientX;
    dragRef.current.startY = e.clientY;
    dragRef.current.origX = position.x;
    dragRef.current.origY = position.y;
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const onMouseMove = (e) => {
    if (!dragRef.current.dragging) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    const newX = Math.max(0, Math.min(window.innerWidth - 360, dragRef.current.origX + dx));
    const newY = Math.max(0, Math.min(window.innerHeight - 420, dragRef.current.origY + dy));
    setPosition({ x: newX, y: newY });
  };

  const onMouseUp = () => {
    if (!dragRef.current.dragging) return;
    dragRef.current.dragging = false;
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
    try {
      localStorage.setItem('enerwiser.debug.globalVariables.posX', String(position.x));
      localStorage.setItem('enerwiser.debug.globalVariables.posY', String(position.y));
    } catch (e) {
      // noop
    }
  };
  
  const subscriptions = getSubscriptions();
  
  const panelStyle = {
    position: 'fixed',
    left: position.x + 'px',
    top: position.y + 'px',
    width: '350px',
    maxHeight: '400px',
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    color: '#00ff00',
    padding: '10px',
    borderRadius: '8px',
    fontSize: '12px',
    fontFamily: 'monospace',
    overflowY: 'auto',
    zIndex: 10000,
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)'
  };
  
  const headerStyle = {
    borderBottom: '1px solid #00ff00',
    paddingBottom: '5px',
    marginBottom: '10px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'move'
  };
  
  const sectionStyle = {
    marginBottom: '15px'
  };
  
  const titleStyle = {
    color: '#ffff00',
    fontWeight: 'bold',
    marginBottom: '5px'
  };
  
  const buttonStyle = {
    backgroundColor: '#00ff00',
    color: '#000',
    border: 'none',
    padding: '3px 8px',
    borderRadius: '3px',
    cursor: 'pointer',
    fontSize: '11px',
    marginLeft: '5px'
  };
  
  // 관리자에서 디버그가 비활성화면 패널/아이콘 둘 다 표시하지 않음
  if (!debugMode) return null;
  if (!isVisible) return null;
  
  return (
    <div style={panelStyle}>
      <div style={headerStyle} onMouseDown={onMouseDownHeader}>
        <span>🔧 Global Variables Debug</span>
        <div>
          <button 
            style={buttonStyle}
            onClick={clearVariables}
          >
            Clear All
          </button>
          <button 
            style={buttonStyle}
            onClick={() => setVisible(false)}
          >
            Hide
          </button>
        </div>
      </div>
      
      <div style={sectionStyle}>
        <div style={titleStyle}>📊 Current Variables ({Object.keys(variables).length})</div>
        {Object.keys(variables).length > 0 ? (
          <pre style={{ margin: 0, color: '#00ffff' }}>
            {JSON.stringify(variables, null, 2)}
          </pre>
        ) : (
          <div style={{ color: '#666' }}>No variables published yet</div>
        )}
      </div>
      
      <div style={sectionStyle}>
        <div style={titleStyle}>📡 Subscriptions ({Object.keys(subscriptions).length})</div>
        {Object.keys(subscriptions).length > 0 ? (
          Object.entries(subscriptions).map(([componentId, vars]) => (
            <div key={componentId} style={{ marginBottom: '5px' }}>
              <div style={{ color: '#ff00ff' }}>{componentId}:</div>
              <div style={{ marginLeft: '10px', color: '#00ffff' }}>
                {Object.entries(vars).map(([varName, config]) => (
                  <div key={varName}>
                    • {varName}: {JSON.stringify(config.default)}
                  </div>
                ))}
              </div>
            </div>
          ))
        ) : (
          <div style={{ color: '#666' }}>No active subscriptions</div>
        )}
      </div>
      
      <div style={{ fontSize: '10px', color: '#666', marginTop: '10px' }}>
        💡 Click charts to publish variables
      </div>
    </div>
  );
};

export default GlobalVariablesDebugPanel;